// frontend/src/components/Requisitions/SearchableSelect.jsx
import React, { useState, useRef, useEffect, useMemo } from 'react';

const normalize = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Dropdown desplegable con la lista COMPLETA visible al abrir.
 * Pensado para catálogos curados (opciones ya específicas de la licitación):
 * al abrir se ve toda la lista para hojear y elegir; no se escribe nada.
 * El buscador es opt-in vía `searchable` (por defecto desactivado).
 * Respeta el mismo contrato que un <select>: onChange recibe el value (id) como string.
 */
export default function SearchableSelect({
  value,
  onChange,
  options, // [{ value, label, search }]
  disabled = false,
  placeholder = 'Seleccione...',
  searchable = false,
  searchPlaceholder = 'Filtrar...',
  emptyText = 'Sin opciones',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!searchable) return options;
    const q = normalize(query).trim();
    if (!q) return options; // sin texto => lista COMPLETA
    const terms = q.split(/\s+/);
    return options.filter((o) => {
      const hay = normalize(o.search || o.label);
      return terms.every((t) => hay.includes(t));
    });
  }, [options, query, searchable]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchable && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open, searchable]);

  const choose = (opt) => {
    onChange(opt ? String(opt.value) : '');
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title={selected ? selected.label : ''}
        className="border p-2 w-full rounded text-left flex justify-between items-center gap-2 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <span className={`truncate ${selected ? '' : 'text-gray-500'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="shrink-0 text-gray-500">▾</span>
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded shadow-lg">
          {searchable && (
            <div className="p-2 border-b">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="border p-2 w-full rounded"
              />
            </div>
          )}
          <ul className="max-h-80 overflow-auto">
            {selected && (
              <li>
                <button
                  type="button"
                  onClick={() => choose(null)}
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-100"
                >
                  — Limpiar selección —
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">{emptyText}</li>
            ) : (
              filtered.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => choose(o)}
                    className={`w-full text-left px-3 py-2 text-sm whitespace-normal break-words hover:bg-gray-100 ${
                      String(o.value) === String(value) ? 'bg-blue-50 font-medium' : ''
                    }`}
                  >
                    {o.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
